// Delete-account confirm gating + error mapping — the web counterpart to
// apps/ios's DeleteAccountViewModel (PreteServer-dev, apps/ios/Prism/UI/
// Settings/DeleteAccountViewModel.swift). Both App Store 5.1.1(v) and
// Google Play's account-deletion policy require this to be initiated with
// an unmistakable confirmation — the existing `confirm()` window.confirm
// dialogs this codebase uses for Revoke/Sign-out-everywhere (a single
// click) aren't enough for a DESTRUCTIVE, IRREVERSIBLE action, so this
// mirrors iOS's type-to-confirm bar exactly rather than inventing a
// separate (weaker or stronger) one for the web surface.
//
// Kept as small pure functions plus one pure async orchestrator — not a
// class — to match this codebase's existing split between pure lib logic
// (see lib/serverLinks.ts) and the page component owning React state.

import { ApiError } from "../api/client";

// Compared case-insensitively with surrounding whitespace trimmed — not a
// byte-exact match — so autocapitalization or a trailing space doesn't
// block a genuine confirmation. Matches DeleteAccountViewModel.confirmationPhrase.
export const DELETE_CONFIRMATION_PHRASE = "DELETE";

export function canConfirmDelete(typed: string): boolean {
  return typed.trim().toUpperCase() === DELETE_CONFIRMATION_PHRASE;
}

// workers/auth/src/routes/account.ts's DELETE /account contract: 409
// has_dependent_accounts is the one documented, actionable rejection — an
// owner with live invited members must remove them first (no self-service
// route for that exists yet either). Map it to what the user should do
// next instead of surfacing the wire error code; everything else falls
// back to the ApiError's own message, then a generic string.
export function messageForDeleteError(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return "This account has other members linked to it. Remove them before deleting your account.";
  }
  if (err instanceof ApiError) return err.message;
  return "Delete failed. Please try again.";
}

export type DeletePhase =
  | { status: "editing" }
  | { status: "deleting" }
  | { status: "failed"; message: string }
  | { status: "deleted" };

// Runs the injected delete call and returns the resulting phase — the
// caller (DeleteAccountPage) sets it as component state and, only on
// `"deleted"`, clears the session and navigates to the terminal page.
// Mirrors DeleteAccountViewModel.confirmDelete(): no-ops (stays
// `"editing"`) when the phrase doesn't match rather than trusting a
// caller-side disabled-button check alone, so a network call can never
// fire on an unconfirmed submit.
export async function runDeleteConfirm(
  typed: string,
  performDelete: () => Promise<void>,
): Promise<DeletePhase> {
  if (!canConfirmDelete(typed)) return { status: "editing" };
  try {
    await performDelete();
    return { status: "deleted" };
  } catch (err) {
    return { status: "failed", message: messageForDeleteError(err) };
  }
}
